import {
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControlLabel,
    Grid,
    Link,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import MaintenancePlanFields from '../../components/printer-models/MaintenancePlanFields';
import {
    createPrinterModel,
    getPrinterModel,
    listMaintenanceTaskTypes,
    listPrinterModels,
    updatePrinterModel,
} from '../../grpc/printerModelsApi';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';

function PrinterModelForm({ isEdit }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [brands, setBrands] = useState([]);
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [avgWatts, setAvgWatts] = useState('');
    const [maxWatts, setMaxWatts] = useState('');
    const [farmLinkSupported, setFarmLinkSupported] = useState(false);
    const [experimental, setExperimental] = useState(true);
    const [taskTypes, setTaskTypes] = useState([]);
    const [maintenanceTasks, setMaintenanceTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isMaster || checkingAccess) {
            return undefined;
        }
        listPrinterModels({ page: 1, limit: 1 })
            .then((response) => {
                const list = response?.data?.brands || [];
                setBrands(Array.isArray(list) ? list : []);
            })
            .catch(() => setBrands([]));
        listMaintenanceTaskTypes()
            .then((types) => setTaskTypes(types))
            .catch(() => setTaskTypes([]));
        return undefined;
    }, [isMaster, checkingAccess]);

    useEffect(() => {
        if (!isMaster || !isEdit || !id) {
            return undefined;
        }
        setIsLoading(true);
        getPrinterModel(id)
            .then((data) => {
                setBrand(data.brand || '');
                setModel(data.model || '');
                setAvgWatts(data.avg_consumption_watts != null ? String(data.avg_consumption_watts) : '');
                setMaxWatts(data.max_consumption_watts != null ? String(data.max_consumption_watts) : '');
                setFarmLinkSupported(Boolean(data.farm_link_supported));
                setExperimental(data.experimental != null ? Boolean(data.experimental) : true);
                setMaintenanceTasks(data.maintenance_tasks || []);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
        return undefined;
    }, [id, isEdit, isMaster]);

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {resultErrors[field]}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextErrors = { has_errors: false };
        const brandValue = brand.trim();
        const modelValue = model.trim();
        const avg = parseInt(avgWatts, 10);
        const max = parseInt(maxWatts, 10);
        if (!brandValue) {
            nextErrors.brand = 'Informe a marca.';
            nextErrors.has_errors = true;
        }
        if (!modelValue) {
            nextErrors.model = 'Informe o modelo.';
            nextErrors.has_errors = true;
        }
        if (Number.isNaN(avg) || avg < 1) {
            nextErrors.avg_consumption_watts = 'Informe o consumo médio em watts.';
            nextErrors.has_errors = true;
        }
        if (Number.isNaN(max) || max < 1) {
            nextErrors.max_consumption_watts = 'Informe o consumo máximo em watts.';
            nextErrors.has_errors = true;
        } else if (!Number.isNaN(avg) && max < avg) {
            nextErrors.max_consumption_watts = 'O consumo máximo deve ser maior ou igual ao médio.';
            nextErrors.has_errors = true;
        }
        if (nextErrors.has_errors) {
            setResultErrors(nextErrors);
            return;
        }
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        const payload = {
            brand: brandValue,
            model: modelValue,
            avg_consumption_watts: avg,
            max_consumption_watts: max,
            farm_link_supported: farmLinkSupported,
            experimental,
            maintenance_tasks: maintenanceTasks,
        };
        const request = isEdit
            ? updatePrinterModel(id, payload)
            : createPrinterModel(payload);
        request
            .then(() => {
                navigate('/admin/printer-models');
            })
            .catch((error) => {
                try {
                    const data = error.response?.data || {};
                    setResultErrors({
                        ...(data.errors || {}),
                        has_errors: true,
                    });
                } catch {
                    setResultErrors({ has_errors: true });
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (checkingAccess) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }
    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin/printer-models">
                    Modelos de impressora
                </Link>
                <Typography color="text.primary">
                    {isEdit ? 'Editando modelo' : 'Adicionando modelo'}
                </Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>
                {isEdit ? 'Editar modelo de impressora' : 'Adicionar modelo de impressora'}
            </Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={brands}
                            value={brand}
                            onInputChange={(_, value) => setBrand(value || '')}
                            renderInput={(params) => (
                                <TextField {...params} label="Marca" />
                            )}
                        />
                        {renderFieldError('brand')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Modelo"
                            fullWidth
                            value={model}
                            onChange={(event) => setModel(event.target.value)}
                        />
                        {renderFieldError('model')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Consumo médio (W)"
                            fullWidth
                            type="number"
                            value={avgWatts}
                            onChange={(event) => setAvgWatts(event.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                        />
                        {renderFieldError('avg_consumption_watts')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Consumo máximo (W)"
                            fullWidth
                            type="number"
                            value={maxWatts}
                            onChange={(event) => setMaxWatts(event.target.value)}
                            inputProps={{ min: 1, step: 1 }}
                        />
                        {renderFieldError('max_consumption_watts')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={farmLinkSupported}
                                    onChange={(event) => setFarmLinkSupported(event.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Já tem suporte no Farm Link"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={experimental}
                                    onChange={(event) => setExperimental(event.target.checked)}
                                    color="warning"
                                />
                            }
                            label="Modelo experimental"
                        />
                    </Grid>
                </Grid>

                <MaintenancePlanFields
                    taskTypes={taskTypes}
                    tasks={maintenanceTasks}
                    onChange={setMaintenanceTasks}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export function NewPrinterModelPage() {
    return <PrinterModelForm isEdit={false} />;
}

export function EditPrinterModelPage() {
    return <PrinterModelForm isEdit />;
}
